/**
 * Bokeh — soft floating glow orbs that drift slowly.
 * Used to add dreamy depth to the background without being visually noisy.
 */

interface Orb {
  x: number
  y: number
  baseX: number
  baseY: number
  r: number
  vx: number
  vy: number
  hue: 'white' | 'cyan' | 'red'
  phase: number
  alpha: number
}

export class Bokeh {
  private orbs: Orb[] = []
  private w = 0
  private h = 0
  private count: number

  constructor(count: number) {
    this.count = count
  }

  resize(w: number, h: number): void {
    this.w = w
    this.h = h
    if (this.orbs.length === 0) this.spawn()
    else {
      // Rescale positions proportionally
      for (const o of this.orbs) {
        o.x = Math.random() * w
        o.y = Math.random() * h
        o.baseX = o.x
        o.baseY = o.y
      }
    }
  }

  private spawn(): void {
    this.orbs = []
    for (let i = 0; i < this.count; i++) {
      const r = 30 + Math.random() * 90
      const hueRoll = Math.random()
      const hue: 'white' | 'cyan' | 'red' =
        hueRoll < 0.6 ? 'white' : hueRoll < 0.85 ? 'cyan' : 'red'
      this.orbs.push({
        x: Math.random() * this.w,
        y: Math.random() * this.h,
        baseX: 0,
        baseY: 0,
        r,
        vx: (Math.random() - 0.5) * 0.12,
        vy: (Math.random() - 0.5) * 0.08,
        hue,
        phase: Math.random() * Math.PI * 2,
        alpha: 0.12 + Math.random() * 0.14,
      })
    }
    for (const o of this.orbs) {
      o.baseX = o.x
      o.baseY = o.y
    }
  }

  tick(dt: number): void {
    const step = dt / 16.666
    for (const o of this.orbs) {
      o.phase += 0.008 * step
      o.x += o.vx * step
      o.y += o.vy * step + Math.sin(o.phase) * 0.15
      if (o.x < -o.r) o.x = this.w + o.r
      else if (o.x > this.w + o.r) o.x = -o.r
      if (o.y < -o.r) o.y = this.h + o.r
      else if (o.y > this.h + o.r) o.y = -o.r
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    ctx.save()
    ctx.globalCompositeOperation = 'screen'
    for (const o of this.orbs) {
      const g = ctx.createRadialGradient(o.x, o.y, 0, o.x, o.y, o.r)
      let c1 = 'rgba(255, 255, 255, '
      if (o.hue === 'cyan') c1 = 'rgba(126, 230, 255, '
      else if (o.hue === 'red') c1 = 'rgba(255, 160, 130, '
      g.addColorStop(0, c1 + o.alpha + ')')
      g.addColorStop(0.5, c1 + o.alpha * 0.4 + ')')
      g.addColorStop(1, c1 + '0)')
      ctx.fillStyle = g
      ctx.fillRect(o.x - o.r, o.y - o.r, o.r * 2, o.r * 2)
    }
    ctx.restore()
  }
}
